import React, { useState, useEffect } from 'react';
import { Modal, Typography, Space, Button } from 'antd';
import { CloseOutlined, MessageOutlined } from '@ant-design/icons';
import { WhatsAppIcon } from '../icons/Whatsapp';
import { Messenger } from '../icons/Messenger';
import { Instagram } from '../icons/Instagram';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

const { Title, Text } = Typography;

const OrderFailedModal = ({ errorType = null, onClose }) => {
  const [showChatOptions, setShowChatOptions] = useState(false);
  const [animateIcons, setAnimateIcons] = useState(false);
  const [arrowAnimation, setArrowAnimation] = useState(false);

  // Reset states when errorType changes (modal opens)
  useEffect(() => {
    if (errorType) {
      setShowChatOptions(false);
      setAnimateIcons(false);
    }
  }, [errorType]);

  useEffect(() => {
    if (showChatOptions) {
      const t = setTimeout(() => setAnimateIcons(true), 60);
      return () => clearTimeout(t);
    }
  }, [showChatOptions]);

  // Arrow animation
  useEffect(() => {
    const interval = setInterval(() => {
      setArrowAnimation(prev => !prev);
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const handleCancel = () => {
    setShowChatOptions(false);
    setAnimateIcons(false);
    onClose();
  };

  const getButtonLabel = () => {
    if (errorType === 'internet') return 'Try Again';
    if (errorType === 'limit') return 'Go to Shop';
    return 'Close';
  };

  const getErrorMessage = () => {
    if (errorType === 'internet') {
      return 'Please check your internet connection and try again.';
    }
    if (errorType === 'limit') {
      return 'You have already placed many orders today. Please try again later or contact customer support.';
    }
    return 'Something went wrong. Please try again later.';
  };

  const styles = `
    @media (prefers-reduced-motion: no-preference) {
      .social-btn {
        opacity: 0;
        transform: translateY(12px) scale(0.9);
        transition: opacity .4s ease, transform .4s cubic-bezier(0.22,1,0.36,1);
      }
      .social-btn.show {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
      .delay-1 { transition-delay: .05s; }
      .delay-2 { transition-delay: .12s; }
      .delay-3 { transition-delay: .18s; }
    }

    .social-btn:hover {
      transform: translateY(-2px) scale(1.1);
    }

    /* Arrow animation */
    .animated-arrow {
      display: inline-block;
      transition: transform 0.3s ease;
    }
    
    .animated-arrow.animate {
      transform: translateX(4px);
    }

    /* Mobile-first tuning */
    @media (max-width: 480px) {
      .ant-modal {
        width: 92% !important;
        max-width: 350px !important;
        margin: 0 12px;
      }
      .lottie-wrap {
        max-width: 220px;
        margin: 0 auto;
      }
    }
  `;

  const primaryBtnStyle = {
    width: '100%',
    height: 44,
    borderRadius: 14,
    fontSize: 15,
    fontWeight: 600,
    backgroundColor: '#111827',
    borderColor: '#111827',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  };

  const socialBtnBase = {
    width: 42,
    height: 42,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 28,
    color: '#fff',
    textDecoration: 'none',
  };

  // Modal opens when errorType is not null
  const isOpen = !!errorType;

  return (
    <>
      <style>{styles}</style>

      <Modal
        open={isOpen}
        onCancel={handleCancel}
        footer={null}
        centered
        width={360}
        closeIcon={<CloseOutlined style={{ fontSize: 18, color: '#9ca3af' }} />}
        style={{ padding: '28px 22px', borderRadius: 20, textAlign: 'center' }}
        afterClose={() => {
          // Ensure all states are reset after modal closes
          setShowChatOptions(false);
          setAnimateIcons(false);
        }}
      >
        <div className="lottie-wrap">
          <DotLottieReact
            src="https://lottie.host/327c8f7c-a3d6-4c6b-83ce-58d9cc6d63d5/txDHWTpU48.lottie"
            loop
            autoplay
          />
        </div>

        <Title
          level={4}
          style={{
            marginBottom: 12,
            fontWeight: 700,
            color: '#111827',
            letterSpacing: '-0.3px',
          }}
        >
          Order Failed!
        </Title>

        <Text
          style={{
            fontSize: 13,
            lineHeight: 1.6,
            color: '#6b7280',
            maxWidth: 280,
            margin: '0 auto',
            display: 'block',
          }}
        >
          {getErrorMessage()}
        </Text>

        <div style={{ marginTop: 32, minHeight: 96 }}>
          {!showChatOptions ? (
            <>
              <Button
                type="primary"
                style={primaryBtnStyle}
                onClick={() => {
                  if (errorType === 'internet') {
                    window.location.reload();
                  } else if (errorType === 'limit'){
                    window.location.href = '/shop';
                  }
                  else {
                    // window.location.href = '/shop';
                    onClose();
                  }
                }}
              >
                {getButtonLabel()}
              </Button>

              {errorType === 'limit' && (
                <Button
                  style={{
                    width: '100%',
                    height: 44,
                    marginTop: 16,
                    borderRadius: 14,
                    background: 'rgba(17,24,39,0.04)',
                    backdropFilter: 'blur(6px)',
                    color: '#111827',
                    fontWeight: 600,
                    fontSize: 15,
                    border: '1px solid rgba(17,24,39,0.12)',
                  }}
                  onClick={() => setShowChatOptions(true)}
                >
                  <MessageOutlined /> Chat with us
                </Button>
              )}
            </>
          ) : (
            <>
              <Text
                style={{
                  display: 'block',
                  marginBottom: 14,
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#6b7280',
                }}
              >
                Select a chat app
              </Text>

              <Space size={20} align="center" wrap>
                <a
                  href="https://wa.me/8801234567890"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`social-btn ${animateIcons ? 'show delay-1' : ''}`}
                  style={socialBtnBase}
                >
                  <WhatsAppIcon />
                </a>

                <a
                  href="https://m.me/yourpage"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`social-btn ${animateIcons ? 'show delay-2' : ''}`}
                  style={socialBtnBase}
                >
                  <Messenger />
                </a>

                <a
                  href="https://ig.me/m/yourprofile"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`social-btn ${animateIcons ? 'show delay-3' : ''}`}
                  style={socialBtnBase}
                >
                  <Instagram />
                </a>
              </Space>

              <Button
                type="link"
                style={{
                  marginTop: 22,
                  fontWeight: 600,
                  color: '#111827',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
                onClick={() => {
                  window.location.href = '/shop';
                }}
              >
                Continue Shopping 
                <div className={`animated-arrow ${arrowAnimation ? 'animate' : ''}`}>→</div>
              </Button>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default OrderFailedModal;